import React, { useEffect, useState } from "react";

// Two install paths in one tiny banner:
//   • Android / Chrome / Edge: capture `beforeinstallprompt` and show our own
//     install button. Tapping it calls `prompt()` so the browser sheet opens.
//   • iOS Safari: there's no native event — show a short hint with the share
//     icon + "Add to Home Screen". User dismisses by tapping ×.
// We persist dismissals in localStorage so we don't nag.

const DISMISS_KEY = "pp_install_dismissed_at";
const DISMISS_DAYS = 14;

const isStandalone = () => {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    // iOS Safari sets this when launched from home-screen
    window.navigator?.standalone === true
  );
};
const isIos = () => {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  // iPad on iOS 13+ identifies as Mac — treat touch-enabled Mac as iOS too.
  return /iPad|iPhone|iPod/.test(ua) || (/Macintosh/.test(ua) && "ontouchend" in document);
};
const recentlyDismissed = () => {
  try {
    const ts = parseInt(localStorage.getItem(DISMISS_KEY) || "0", 10);
    if (!ts) return false;
    return Date.now() - ts < DISMISS_DAYS * 24 * 60 * 60 * 1000;
  } catch {
    return false;
  }
};

export default function InstallAppBanner() {
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);
  const [iosHint, setIosHint] = useState(false);

  useEffect(() => {
    if (isStandalone() || recentlyDismissed()) return;

    // Android / Chromium path
    const onBefore = (e) => {
      e.preventDefault();
      setDeferred(e);
      setShow(true);
    };
    window.addEventListener("beforeinstallprompt", onBefore);
    window.addEventListener("appinstalled", () => {
      setShow(false);
      setDeferred(null);
      try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch {}
    });

    // iOS path — show hint only after a short delay so it doesn't compete
    // with first paint, and only on a touchable iOS device.
    if (isIos()) {
      const t = setTimeout(() => { setShow(true); setIosHint(true); }, 1500);
      return () => { window.removeEventListener("beforeinstallprompt", onBefore); clearTimeout(t); };
    }
    return () => window.removeEventListener("beforeinstallprompt", onBefore);
  }, []);

  if (!show) return null;

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch {}
    setShow(false);
  };

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    try {
      const { outcome } = await deferred.userChoice;
      if (outcome === "dismissed") dismiss();
      else { setShow(false); setDeferred(null); }
    } catch {
      dismiss();
    }
  };

  return (
    <div
      className="rounded-2xl ring-1 ring-violet-200 bg-gradient-to-r from-violet-50 via-white to-fuchsia-50 px-4 sm:px-5 py-3 sm:py-4 shadow-[0_8px_24px_-20px_rgba(139,92,246,0.4)]"
      data-testid="install-app-banner"
      data-mode={iosHint ? "ios" : "android"}
    >
      <div className="flex items-center gap-3 sm:gap-4">
        <div className="shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-violet-100 text-violet-700 flex items-center justify-center text-xl sm:text-2xl" aria-hidden>
          📲
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-display text-[14px] sm:text-base font-bold text-violet-800 leading-tight">
            {iosHint ? "Pin Prime Pillar to your home screen" : "Install Prime Pillar as an app"}
          </div>
          <p className="mt-0.5 text-[11.5px] sm:text-[12.5px] text-zinc-600 leading-snug line-clamp-2">
            {iosHint
              ? <>Tap <span className="font-semibold">Share</span> ↗ in Safari, then <span className="font-semibold">Add to Home Screen</span> — opens like a native app, no app store.</>
              : "One-tap launch, full-screen view, and (soon) push alerts for high-score signals. No app store."}
          </p>
        </div>
        <div className="shrink-0 flex items-center gap-1.5">
          {!iosHint && (
            <button
              type="button"
              onClick={install}
              className="text-[12px] font-semibold px-3 py-1.5 rounded-full bg-violet-600 text-white hover:bg-violet-700 transition"
              data-testid="install-app-btn"
            >
              Install
            </button>
          )}
          <button
            type="button"
            onClick={dismiss}
            aria-label="Dismiss"
            className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-white ring-1 ring-zinc-200 text-zinc-500 hover:text-zinc-800 hover:bg-zinc-50 transition flex items-center justify-center text-sm"
            data-testid="install-app-dismiss-btn"
          >
            ×
          </button>
        </div>
      </div>
    </div>
  );
}
